import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { httpGet } from "../helpers/httpMethods";
import SearchHighlight from "./SearchHighlight";
import SearchPaywall from "./SearchPaywall";
import RequestIssueModal from "../Archive/RequestIssueModal";
import { parseIssueQuery, formatDay } from "../Archive/parseIssueQuery";
import "./navSearch.css";

const EMPTY = { publications: [], stories: [] };
const ACCESS_LABEL = { paid: "Subscribers", entitled: "Subscribed", free: "Free to read" };

const shortDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  } catch (e) {
    return "";
  }
};

export default function NavSearch({ browserRouter, isLoggedIn }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(EMPTY);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState(null);
  const [paywall, setPaywall] = useState(null);
  const [guess, setGuess] = useState(null);
  const [reqOpen, setReqOpen] = useState(false);

  const wrapRef = useRef(null);
  const dropRef = useRef(null);
  const timer = useRef(null);
  const latest = useRef("");

  const pubs = (results.publications || []).slice(0, 3);
  const stories = results.stories || [];
  const items = [...pubs.map((p) => ({ type: "pub", p })), ...stories.map((s) => ({ type: "story", s }))];
  const query = text.trim();

  const runSearch = async (value) => {
    latest.current = value;
    const q = value.trim();
    if (!q) {
      setResults(EMPTY);
      setLoading(false);
      return;
    }
    const res = await httpGet(`search?q=${encodeURIComponent(q)}&limit=5`);
    if (latest.current !== value) return; // a newer search has started
    setLoading(false);
    setActive(-1);
    if (res && res.code === 200 && res.data) setResults(res.data);
    else setResults({ ...EMPTY, error: true });
  };

  const onChange = (e) => {
    const value = e.target.value;
    setText(value);
    setOpen(true);
    setLoading(Boolean(value.trim()));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => runSearch(value), 300);
  };

  const clear = () => {
    clearTimeout(timer.current);
    latest.current = "";
    setText("");
    setResults(EMPTY);
    setLoading(false);
    setOpen(false);
  };

  const openPublication = (p) => {
    setOpen(false);
    browserRouter(`/publication/${p.name}`);
  };

  const openStory = (s) => {
    setOpen(false);
    if (s.access === "paid") {
      setPaywall(s);
      return;
    }
    browserRouter(`/view_single_paper/${s.editionId}${s.pageNumber ? `?page=${s.pageNumber}` : ""}`);
  };

  const choose = (item) => (item.type === "pub" ? openPublication(item.p) : openStory(item.s));

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      choose(items[active]);
    }
  };

  // Keep the dropdown anchored under the input.
  useLayoutEffect(() => {
    if (!open || !query || !wrapRef.current) return;
    const update = () => {
      const r = wrapRef.current.getBoundingClientRect();
      const width = Math.min(Math.max(r.width, 560), window.innerWidth - 32);
      const left = Math.max(16, Math.min(r.left, window.innerWidth - width - 16));
      setPos({ top: r.bottom + 8, left, width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, query]);

  // Close on click outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (dropRef.current && dropRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => () => clearTimeout(timer.current), []);

  // Nothing found: guess the paper and date from what was typed, for "Request this issue".
  useEffect(() => {
    const empty = !(results.publications || []).length && !(results.stories || []).length;
    if (loading || results.error || query.length < 3 || !empty) {
      setGuess(null);
      return undefined;
    }
    const { date, rest } = parseIssueQuery(query);
    setGuess({ pub: null, date });
    if (rest.length < 2) return undefined;
    let live = true;
    httpGet(`search?q=${encodeURIComponent(rest)}&type=publications&limit=1`).then((r) => {
      const p = r && r.data && Array.isArray(r.data.publications) ? r.data.publications[0] : null;
      if (live && p) setGuess((g) => ({ ...(g || {}), pub: { id: p.id, name: (p.name || "").trim(), logo: p.logo } }));
    });
    return () => {
      live = false;
    };
  }, [results, loading, query]);

  const dropdown = (
    <div ref={dropRef} id="ns-results" className="ns-drop" style={pos || {}}>
      {pubs.length > 0 && (
        <>
          <div className="ns-group">Publications</div>
          {pubs.map((p, i) => (
            <button
              type="button"
              key={p.id}
              className={`ns-row ns-pub${active === i ? " is-active" : ""}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => openPublication(p)}
            >
              <span className="ns-logo">
                {p.logo ? <img src={p.logo} alt="" /> : <span>{(p.name || "?").trim().charAt(0)}</span>}
              </span>
              <span className="ns-pub-name">{(p.name || "").trim()}</span>
              <span className="ns-arrow" aria-hidden="true">→</span>
            </button>
          ))}
        </>
      )}

      {stories.length > 0 && (
        <>
          <div className="ns-group">Stories in recent editions</div>
          {stories.map((s, j) => {
            const i = pubs.length + j;
            return (
              <button
                type="button"
                key={`${s.editionId}-${s.pageNumber || s.articleId}`}
                className={`ns-row ns-story${active === i ? " is-active" : ""}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => openStory(s)}
              >
                {s.title && <span className="ns-title">{s.title}</span>}
                <span className={s.title ? "ns-snippet" : "ns-snippet ns-snippet-main"}>
                  …<SearchHighlight text={s.snippet} />…
                </span>
                <span className="ns-meta">
                  <span className="ns-meta-pub">{s.publication.name}</span>
                  <span>· {shortDate(s.publishedDate)}</span>
                  {s.pageNumber ? <span>· Page {s.pageNumber}</span> : null}
                  <span className={`ns-access ns-access-${s.access}`}>{ACCESS_LABEL[s.access]}</span>
                </span>
              </button>
            );
          })}
        </>
      )}

      {results.error && <div className="ns-status">Search is unavailable right now. Please try again.</div>}
      {!results.error && loading && !items.length && <div className="ns-status">Searching…</div>}
      {!results.error && !loading && !items.length && (
        <div className="ns-status">
          No results for “{query}”
          {query.length < 3 ? <span className="ns-hint">Type at least 3 letters to search stories</span> : null}
        </div>
      )}
      {!results.error && !loading && !items.length && query.length >= 3 && (
        <div className="ns-reqcard">
          <span className="ns-reqcard-title">Looking for a specific issue?</span>
          <span className="ns-reqcard-text">We’ll ask the publisher and email you when it’s ready.</span>
          {guess && (guess.pub || guess.date) ? (
            <span className="ns-reqcard-guess">
              We understood: {guess.pub ? <b>{guess.pub.name}</b> : null}
              {guess.pub && guess.date ? " · " : null}
              {guess.date ? <b>{formatDay(guess.date)}</b> : null}
            </span>
          ) : null}
          <span className="ns-reqcard-actions">
            <button type="button" className="ns-reqcard-btn" onClick={() => { setOpen(false); setReqOpen(true); }}>Request this issue</button>
            <button type="button" className="ns-reqcard-btn is-outline" onClick={() => { setOpen(false); browserRouter("/archive"); }}>Browse the archive</button>
          </span>
          <span className="ns-reqcard-note">Free to request · you only pay when it’s available</span>
        </div>
      )}
      {stories.length > 0 && <div className="ns-foot">↑ ↓ to move · Enter to open · Esc to close</div>}
    </div>
  );

  return (
    <div className="nav-search-wrap" ref={wrapRef} style={{ position: "relative" }}>
      <span className="nav_search_app">
        <i className="fa fa-search nav-search-icon" aria-hidden="true"></i>
        <input
          type="text"
          role="combobox"
          aria-label="Search papers and stories"
          aria-expanded={open}
          aria-controls="ns-results"
          aria-autocomplete="list"
          placeholder="Search papers and stories..."
          value={text}
          onChange={onChange}
          onFocus={() => query && setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ fontSize: "13px" }}
        />
        {text && (
          <button type="button" className="nav-search-clear" aria-label="Clear search" onClick={clear}>
            ✕
          </button>
        )}
      </span>

      {open && query && pos && createPortal(dropdown, document.body)}

      {reqOpen && (
        <RequestIssueModal
          onClose={() => setReqOpen(false)}
          history={{ push: browserRouter }}
          initialPublication={guess && guess.pub}
          initialDates={guess && guess.date ? [guess.date] : []}
        />
      )}

      {paywall && (
        <SearchPaywall
          story={paywall}
          isLoggedIn={isLoggedIn}
          onClose={() => setPaywall(null)}
          onSubscribe={() => {
            const name = paywall.publication.name;
            setPaywall(null);
            browserRouter(`/publication/${name}`);
          }}
          onLogin={() => {
            const name = paywall.publication.name;
            setPaywall(null);
            // Same mechanism the publication page uses: return here after login.
            localStorage.setItem("comingFromPublicPublication", "true");
            localStorage.setItem("rediretURL", name);
            browserRouter("/login");
          }}
        />
      )}
    </div>
  );
}
